import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ChangeEvent,
  type FocusEvent,
  type InputHTMLAttributes,
  type KeyboardEvent,
} from 'react';
import { CalendarDays } from 'lucide-react';
import { cx } from '@/lib/cx';
import { toWareki, weekdayJa } from '@/lib/wareki';
import { formatDateText, isCompleteDateText, isIsoValue, isWithinRange, parseDateText, type DateKind } from '@/lib/dateInput';
import { DateCalendar } from './DateCalendar';

export interface DateFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size' | 'value' | 'defaultValue' | 'min' | 'max'> {
  /** 値（ISO `YYYY-MM-DD`、month 種別は `YYYY-MM`）。指定すると制御コンポーネント */
  value?: string | null;
  defaultValue?: string | null;
  invalid?: boolean;
  /** 入力欄の右に和暦（令和8年4月1日（水））を表示 */
  showWareki?: boolean;
  /** 'date'（既定）| 'month'（年月のみ） */
  kind?: DateKind;
  /** 'md'（既定・高さ 34px）| 'sm'（表のセル用・高さ 28px） */
  size?: 'md' | 'sm';
  /** 選択・入力できる最小値（ISO） */
  min?: string | number;
  /** 選択・入力できる最大値（ISO） */
  max?: string | number;
}

type ValueDescriptor = { get: () => string; set: (v: string) => void };

/** hidden 要素の「イベントを出さない」書き込み口（value プロパティを包む前の setter） */
const rawValue = new WeakMap<HTMLInputElement, ValueDescriptor>();
/** hidden 要素へ外部（react-hook-form の reset / setValue 等）から書き込まれたときの通知先 */
const externalWrite = new WeakMap<HTMLInputElement, (v: string) => void>();

/**
 * hidden 要素の value プロパティを包み、外部からの `el.value = ...` を検知できるようにする。
 * react-hook-form は register() の ref 要素の value を直接読み書きする（reset() などはイベントを出さない）ため。
 */
function patchHidden(el: HTMLInputElement, onWrite: (v: string) => void, focusVisible: () => HTMLInputElement | null) {
  externalWrite.set(el, onWrite);
  if (rawValue.has(el)) return;
  const own = Object.getOwnPropertyDescriptor(el, 'value');
  const proto = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
  const base = own?.get && own.set ? own : proto;
  if (!base?.get || !base.set) return;
  const get = base.get;
  const set = base.set;
  rawValue.set(el, { get: () => get.call(el) as string, set: (v) => set.call(el, v) });
  Object.defineProperty(el, 'value', {
    configurable: true,
    enumerable: true,
    get: () => get.call(el) as string,
    set: (v: unknown) => {
      const s = v == null ? '' : String(v);
      set.call(el, s);
      externalWrite.get(el)?.(s);
    },
  });
  // エラー時のフォーカス（RHF の shouldFocusError / setFocus）は表示用の入力欄へ
  el.focus = (opts?: FocusOptions) => focusVisible()?.focus(opts);
  el.select = () => focusVisible()?.select();
}

function writeSilently(el: HTMLInputElement | null, v: string) {
  if (!el) return;
  const raw = rawValue.get(el);
  if (raw) {
    if (raw.get() !== v) raw.set(v);
  } else if (el.value !== v) el.value = v;
}

/** onChange / onBlur に渡すイベント相当（target は ISO 値を持つ hidden 要素） */
function makeEvent(el: HTMLInputElement, type: 'change' | 'blur'): ChangeEvent<HTMLInputElement> & FocusEvent<HTMLInputElement> {
  const nativeEvent = new Event(type, { bubbles: type === 'change' });
  return {
    target: el,
    currentTarget: el,
    relatedTarget: null,
    type,
    nativeEvent,
    bubbles: nativeEvent.bubbles,
    cancelable: false,
    defaultPrevented: false,
    eventPhase: 2,
    isTrusted: false,
    timeStamp: Date.now(),
    preventDefault: () => undefined,
    isDefaultPrevented: () => false,
    stopPropagation: () => undefined,
    isPropagationStopped: () => false,
    persist: () => undefined,
  } as unknown as ChangeEvent<HTMLInputElement> & FocusEvent<HTMLInputElement>;
}

function isInsideOf(node: EventTarget | null, ...roots: (HTMLElement | null)[]): boolean {
  return node instanceof Node && roots.some((r) => !!r?.contains(node));
}

function normalizeValue(v: unknown, kind: DateKind): string {
  if (v == null) return '';
  const s = String(v);
  if (s === '' || isIsoValue(s, kind)) return s;
  // `2026-10-21T00:00:00` などは日付部分だけを採る
  const head = s.slice(0, kind === 'month' ? 7 : 10);
  return isIsoValue(head, kind) ? head : s;
}

/**
 * 日付入力（独自 DatePicker）。値は ISO 形式 `YYYY-MM-DD`（API の date 型と同じ。month 種別は `YYYY-MM`）。
 *
 * - 表示は `YYYY/MM/DD` の text 入力＋カレンダーボタン。手入力は `2026/10/21` `2026-10-21` `20261021` `2026/1/5`
 *   `R8.10.21` `令和8年10月21日` を受け付け、blur / Enter で `YYYY/MM/DD` に整える。
 * - 不正な日付（`2026/02/30` 等）・min/max 外は確定しない。Enter 時は入力を残して赤枠、blur 時は直前の値に戻して通知。
 * - register()：ref と name は ISO 値を持つ hidden の input に付く。RHF が `el.value = ...` で書き込むと表示に反映する。
 * - 値が変わると `{ target: <hidden input> }` のイベントで onChange を呼ぶ（`e.target.value` は ISO 値）。
 */
export const DateField = forwardRef<HTMLInputElement, DateFieldProps>(function DateField(
  {
    invalid,
    showWareki,
    kind = 'date',
    size = 'md',
    className,
    style,
    onChange,
    onBlur,
    onFocus,
    onKeyDown,
    value,
    defaultValue,
    name,
    min,
    max,
    disabled,
    readOnly,
    required,
    form,
    placeholder,
    id,
    ...rest
  },
  ref,
) {
  const uid = useId().replace(/:/g, '');
  const popupId = `dp${uid}`;
  const isControlled = value !== undefined;
  const [innerValue, setInnerValueState] = useState(() => normalizeValue(defaultValue, kind));
  /** 非制御時の最新値（state の反映を待たずに hidden 要素へ書くため） */
  const innerRef = useRef(innerValue);
  const setInnerValue = (v: string) => {
    innerRef.current = v;
    setInnerValueState(v);
  };
  const current = isControlled ? normalizeValue(value, kind) : innerValue;

  /** 編集中（フォーカスあり）の入力文字列。null = 値を整形して表示 */
  const [draft, setDraft] = useState<string | null>(null);
  /** 不正入力の通知（Enter で確定できなかった・blur で元に戻した） */
  const [notice, setNotice] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const hiddenRef = useRef<HTMLInputElement | null>(null);
  const textRef = useRef<HTMLInputElement | null>(null);
  const controlRef = useRef<HTMLSpanElement>(null);
  const wrapRef = useRef<HTMLSpanElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  // 最新の props / state をコールバックから参照する
  const latest = useRef({ isControlled, current, kind, onChange, onBlur });
  latest.current = { isControlled, current, kind, onChange, onBlur };

  // 編集中に外部から値が変わったら、入力文字列も追随させる（入力文字列が同じ値を表している場合はそのまま）
  const [prevCurrent, setPrevCurrent] = useState(current);
  if (prevCurrent !== current) {
    setPrevCurrent(current);
    if (draft !== null) {
      const p = parseDateText(draft, kind);
      const same = (p.status === 'ok' && p.value === current) || (p.status === 'empty' && current === '');
      if (!same) setDraft(formatDateText(current, kind));
    }
  }

  // hidden 要素の値を常に現在値へ（制御コンポーネントで親が値を受け付けなかった場合も戻す）
  useLayoutEffect(() => {
    writeSilently(hiddenRef.current, isControlled ? current : innerRef.current);
  });

  const onExternalWrite = useCallback((v: string) => {
    const l = latest.current;
    if (l.isControlled) return;
    const next = normalizeValue(v, l.kind);
    innerRef.current = next;
    setInnerValueState(next);
    setNotice(null);
  }, []);

  const setHidden = useCallback(
    (el: HTMLInputElement | null) => {
      if (el) {
        patchHidden(el, onExternalWrite, () => textRef.current);
        // RHF が ref 登録時に読む初期値（defaultValue 指定時）
        if (rawValue.get(el)?.get() === '' && latest.current.current) writeSilently(el, latest.current.current);
      }
      hiddenRef.current = el;
      if (typeof ref === 'function') ref(el);
      else if (ref) ref.current = el;
    },
    [ref, onExternalWrite],
  );

  const minV = min === undefined || min === '' ? undefined : String(min);
  const maxV = max === undefined || max === '' ? undefined : String(max);

  /** 値を確定する（変化したときだけ onChange） */
  const commit = (next: string) => {
    const l = latest.current;
    if (next === l.current) return;
    if (!l.isControlled) setInnerValue(next);
    const el = hiddenRef.current;
    if (!el) return;
    writeSilently(el, next);
    l.onChange?.(makeEvent(el, 'change'));
  };

  /**
   * 入力文字列を解釈して確定する。
   * @returns 'ok'（確定・空を含む）| 'invalid'（不正・範囲外）
   */
  const commitText = (text: string): 'ok' | 'invalid' => {
    const p = parseDateText(text, kind);
    if (p.status === 'empty') {
      commit('');
      return 'ok';
    }
    if (p.status === 'ok' && isWithinRange(p.value, minV, maxV)) {
      commit(p.value);
      return 'ok';
    }
    return 'invalid';
  };

  /** 不正入力の通知文。reverted = blur で元の値に戻した */
  const invalidMessage = (text: string, reverted: boolean) => {
    const p = parseDateText(text, kind);
    const what = kind === 'month' ? '年月' : '日付';
    if (p.status === 'ok') {
      const range = `${minV ? formatDateText(minV, kind) : ''}〜${maxV ? formatDateText(maxV, kind) : ''}`;
      return reverted ? `「${text}」は入力できる範囲（${range}）外のため、元の値に戻しました` : `${range} の範囲で入力してください`;
    }
    return reverted
      ? `「${text}」は${what}として正しくないため、元の値に戻しました`
      : `${what}を正しく入力してください（例：${kind === 'month' ? '2026/10' : '2026/10/21'}）`;
  };

  /** フォーカスが部品（入力欄・ボタン・カレンダー）の外へ出た */
  const leave = () => {
    setOpen(false);
    if (draft !== null) {
      const text = draft;
      setDraft(null);
      if (commitText(text) === 'invalid') setNotice(invalidMessage(text, true));
    }
    const el = hiddenRef.current;
    if (el) latest.current.onBlur?.(makeEvent(el, 'blur'));
  };

  const isInside = (node: EventTarget | null) => isInsideOf(node, wrapRef.current, popupRef.current);

  const openCalendar = () => {
    if (disabled || readOnly) return;
    if (draft !== null && commitText(draft) === 'ok') {
      setDraft(null);
      setNotice(null);
    }
    setOpen(true);
  };

  const closeCalendar = (focusInput: boolean) => {
    setOpen(false);
    if (focusInput) textRef.current?.focus();
  };

  // 外側クリックで閉じる
  useLayoutEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!isInsideOf(e.target, wrapRef.current, popupRef.current)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown, true);
    return () => document.removeEventListener('mousedown', onDown, true);
  }, [open]);

  // blur で元に戻した旨の通知は一定時間で消す
  useEffect(() => {
    if (!notice || draft !== null) return;
    const t = window.setTimeout(() => setNotice(null), 8000);
    return () => window.clearTimeout(t);
  }, [notice, draft]);

  const shown = draft ?? formatDateText(current, kind);
  const draftInvalid = notice !== null;
  const isInvalid = !!invalid || draftInvalid;

  const wareki =
    showWareki && current && isIsoValue(current, kind)
      ? kind === 'month'
        ? toWareki(`${current}-01`, { style: 'month' })
        : `${toWareki(current)}（${weekdayJa(current)}）`
      : '';

  return (
    <span
      ref={wrapRef}
      className={cx('bs-date', size === 'sm' && 'bs-date--sm', kind === 'month' && 'bs-date--month', className)}
      style={style}
      onBlur={(e) => {
        if (!isInside(e.relatedTarget)) leave();
      }}
    >
      <span
        ref={controlRef}
        className={cx('bs-date__control', isInvalid && 'is-invalid', disabled && 'is-disabled', readOnly && 'is-readonly', open && 'is-open')}
      >
        <input
          {...rest}
          ref={textRef}
          id={id}
          type="text"
          className="bs-input bs-date__input"
          value={shown}
          placeholder={placeholder ?? (kind === 'month' ? '年/月' : '年/月/日')}
          autoComplete="off"
          spellCheck={false}
          disabled={disabled}
          readOnly={readOnly}
          aria-invalid={isInvalid || undefined}
          aria-required={rest['aria-required'] ?? (required || undefined)}
          aria-controls={open ? popupId : rest['aria-controls']}
          data-kind={kind}
          onFocus={(e) => {
            if (draft === null) setDraft(formatDateText(current, kind));
            setNotice(null);
            onFocus?.(e);
          }}
          onChange={(e) => {
            const text = e.target.value;
            setDraft(text);
            setNotice(null);
            // 桁のそろった完全な形式（2026/10/21・20261021）か空なら、その場で確定する
            if (text.trim() === '' || isCompleteDateText(text, kind)) {
              const p = parseDateText(text, kind);
              if (p.status === 'empty' || (p.status === 'ok' && isWithinRange(p.value, minV, maxV))) commitText(text);
            }
          }}
          onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
            onKeyDown?.(e);
            if (e.defaultPrevented) return;
            if (e.altKey && (e.key === 'ArrowDown' || e.key === 'Down')) {
              e.preventDefault();
              openCalendar();
              return;
            }
            if (e.key === 'Escape' && open) {
              e.preventDefault();
              e.stopPropagation();
              closeCalendar(true);
              return;
            }
            if (e.key === 'Enter' && !readOnly && !disabled) {
              const text = draft ?? shown;
              if (commitText(text) === 'invalid') {
                // 確定できない：フォーム送信もしない
                e.preventDefault();
                setNotice(invalidMessage(text, false));
                return;
              }
              const p = parseDateText(text, kind);
              setDraft(p.status === 'ok' ? formatDateText(p.value, kind) : '');
              setNotice(null);
              if (open) setOpen(false);
            }
          }}
        />
        <button
          type="button"
          className="bs-date__button"
          aria-label="カレンダーを開く"
          title="カレンダーを開く（Alt+↓）"
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-controls={open ? popupId : undefined}
          tabIndex={-1}
          disabled={disabled || readOnly}
          onMouseDown={(e) => {
            // 入力欄のフォーカスを奪わずに開閉する（クリック後はカレンダーへフォーカスが移る）
            e.preventDefault();
          }}
          onClick={() => (open ? closeCalendar(true) : openCalendar())}
        >
          <CalendarDays size={size === 'sm' ? 14 : 16} aria-hidden />
        </button>
      </span>
      <input ref={setHidden} type="hidden" name={name} form={form} disabled={disabled} readOnly={readOnly} className="bs-date__value" />
      {showWareki && (
        <span className="bs-date__wareki" aria-live="polite">
          {wareki}
        </span>
      )}
      {notice && (
        <span className="bs-date__notice" role="alert">
          {notice}
        </span>
      )}
      {open && (
        <DateCalendar
          id={popupId}
          kind={kind}
          value={isIsoValue(current, kind) ? current : ''}
          min={minV}
          max={maxV}
          anchorRef={controlRef}
          popupRef={popupRef}
          onSelect={(v) => {
            commit(v);
            setDraft(null);
            setNotice(null);
            closeCalendar(true);
          }}
          onClear={() => {
            commit('');
            setDraft(null);
            setNotice(null);
            closeCalendar(true);
          }}
          onClose={() => closeCalendar(true)}
        />
      )}
    </span>
  );
});
